import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { StoredOrderRuleParseResult } from "@/features/line-sheet-sets/hooks/use-stored-order-rule-parse-result";

interface OrderRuleParseChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  stored: StoredOrderRuleParseResult | null;
  orderRuleFiles?: { id: number; name: string }[];
  // 저장된 파싱 결과를 그대로 적용
  onApplyStored: () => void;
  // 새로 파싱 (파일 선택 등 기존 파싱 흐름 시작)
  onReparse: () => void;
}

function formatParsedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// 파싱 버튼을 눌렀을 때 저장된 결과가 있으면 띄우는 선택 모달.
// "저장된 결과 적용" 또는 "새로 파싱" 중 하나를 고른다.
export default function OrderRuleParseChoiceModal({
  isOpen,
  onClose,
  stored,
  orderRuleFiles,
  onApplyStored,
  onReparse,
}: OrderRuleParseChoiceModalProps) {
  const { tTitle } = useI18nHelper();

  const fileName =
    stored?.unoFileId != null
      ? orderRuleFiles?.find((f) => f.id === stored.unoFileId)?.name
      : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"md"} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
        >
          {tTitle("parse_order_rules")}
        </ModalHeader>
        <ModalBody>
          <Text fontSize={"12px"} color={"#6F6F6C"} marginBottom={"12px"}>
            {tTitle("order_rule_parse_choice_description")}
          </Text>
          {stored && (
            <Box
              padding={"10px 12px"}
              borderRadius={"6px"}
              border={"0.5px solid #E5DCA0"}
              background={"#FFF7CC"}
            >
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                marginBottom={"4px"}
              >
                <Text fontSize={"12px"} fontWeight={"700"} color={"#7A6A00"}>
                  {tTitle("saved_parse_result")}
                </Text>
                <Text fontSize={"11px"} color={"#7A6A00"}>
                  {formatParsedAt(stored.parsedAt)}
                </Text>
              </Flex>
              <Text fontSize={"11px"} color={"#444444"}>
                {stored.result.brands.length} {tTitle("brand")}
                {stored.result.unmatched && stored.result.unmatched.length > 0
                  ? ` · ${tTitle("unmatched")} ${stored.result.unmatched.length}`
                  : ""}
              </Text>
              {fileName && (
                <Text
                  fontSize={"11px"}
                  color={"#6F6F6C"}
                  marginTop={"2px"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                  whiteSpace={"nowrap"}
                >
                  {fileName}
                </Text>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton
            variant={"normal"}
            onClick={() => {
              onReparse();
            }}
          >
            {tTitle("reparse_order_rules")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={!stored}
            onClick={() => {
              onApplyStored();
            }}
          >
            {tTitle("apply_saved_parse_result")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
